<!-- 商品管理 V2 方案 - 附加服务（OPTION）配置 -->
<template>
  <section class="product-option-settings">
    <div class="section-title">
      <h2>附加服务</h2>
      <div>
        <el-button class="blue-button" @click="$refs.systemOptionDialog.open(options, userId)">选择系统 option</el-button>
        <el-button class="green-button" @click="addCustomOption">创建自定义 option</el-button>
      </div>
    </div>

    <!-- OPTION 列表：中/日文名、价格、计费、媒体附件 -->
    <div class="option-table">
      <div class="option-grid table-head">
        <div>下单使用</div>
        <div>附加服务中文名</div>
        <div>附加服务日文名</div>
        <div>收费标准</div>
        <div>计费方式</div>
        <div>人效比（秒）</div>
        <div>照片</div>
        <div>视频</div>
        <div>文件</div>
        <div>特殊要求</div>
        <div>操作</div>
      </div>
      <div v-for="(option, optionIndex) in options" :key="option.id || optionIndex" class="option-grid table-row">
        <div>
          <el-switch v-model="option.checked" active-text="开" inactive-text="关" />
        </div>
        <div class="name-cell"><span>{{ optionIndex + 1 }}</span><el-input v-model="option.name" /></div>
        <div><el-input v-model="option.name_ja" /></div>
        <div class="price-cell"><el-input v-model="option.price" /><span>{{ option.unit }}</span></div>
        <div class="billing-cell">
          <el-radio v-model="option.price_type" :label="0" @change="option.unit = '元'">单价</el-radio>
          <el-radio v-model="option.price_type" :label="1" @change="option.unit = '%'">百分比</el-radio>
        </div>
        <div><el-input v-model="option.work_seconds" type="number" min="0" @keydown.native="preventInvalidNumberKey($event, 0)" @paste.native="preventInvalidNumberPaste($event, 0)" @input="setNonNegative(option, 'work_seconds', $event)" @blur="setNonNegative(option, 'work_seconds', $event.target.value)" /></div>
        <div class="upload-cell">
          <div class="media-list">
            <div v-for="(url, imageIndex) in option.images" :key="imageIndex" class="media-item">
              <el-image :src="url" fit="cover" :preview-src-list="option.images" />
              <button type="button" @click="option.images.splice(imageIndex, 1)"><i class="el-icon-close"></i></button>
            </div>
          </div>
          <el-upload action="#" accept=".jpg,.jpeg,.png" multiple :show-file-list="false" :http-request="file => uploadImage(file, option)">
            <el-button type="text">上传图片</el-button>
          </el-upload>
        </div>
        <div class="upload-cell">
          <div class="media-list">
            <div v-for="(video, videoIndex) in option.videos" :key="videoIndex" class="media-item video-item">
              <video
                :src="getVideoUrl(video)"
                :poster="getVideoPoster(video)"
                controls
                preload="auto"
                playsinline
                @loadeddata="generateVideoPoster(getVideoUrl(video), $event)"
              ></video>
              <button type="button" @click="option.videos.splice(videoIndex, 1)"><i class="el-icon-close"></i></button>
            </div>
          </div>
          <el-upload action="#" accept="video/*" multiple :show-file-list="false" :http-request="file => uploadVideo(file, option)">
            <el-button type="text">上传视频</el-button>
          </el-upload>
        </div>
        <div class="upload-cell file-cell">
          <div v-for="(file, fileIndex) in option.files" :key="fileIndex" class="file-row">
            <a :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name }}</a>
            <button type="button" @click="option.files.splice(fileIndex, 1)"><i class="el-icon-close"></i></button>
          </div>
          <el-upload action="#" multiple :show-file-list="false" :http-request="file => uploadFile(file, option)">
            <el-button type="text">上传</el-button>
          </el-upload>
        </div>
        <div><el-input v-model="option.special_requirement" type="textarea" :rows="4" resize="none" /></div>
        <div><button type="button" class="delete-option" @click="options.splice(optionIndex, 1)">删除</button></div>
      </div>
    </div>

    <system-option-dialog ref="systemOptionDialog" @confirm="addSystemOptions" />
  </section>
</template>

<script>
import SystemOptionDialog from './systemOptionDialog'

/**
 * 贩售方案附加服务编辑
 * 支持从系统 OPTION 库选取或自定义创建，含图片/视频/文件上传
 */
export default {
  name: 'ProductOptionSettings',
  components: { SystemOptionDialog },
  props: {
    options: { type: Array, required: true },
    userId: { type: [Number, String], required: true }
  },
  data() {
    return {
      videoPosters: {}
    }
  },
  methods: {
    getVideoUrl(video) {
      return typeof video === 'string' ? video : ((video && (video.url || video.video_url)) || '')
    },
    getVideoPoster(video) {
      const url = this.getVideoUrl(video)
      return (video && typeof video === 'object' && (video.cover || video.poster)) || this.videoPosters[url] || ''
    },
    generateVideoPoster(url, event) {
      if (!url || this.videoPosters[url]) return
      const video = event.target
      if (!video.videoWidth || !video.videoHeight) return
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      try {
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
        this.$set(this.videoPosters, url, canvas.toDataURL('image/jpeg', 0.8))
      } catch (error) {
        // 跨域视频无法截取首帧时保留原视频播放功能
      }
    },
    sanitizeNonNegative(value, decimals = 2) {
      if (value === '' || value == null) return ''
      const text = String(value).replace(/[^\d.]/g, '')
      const dotIndex = text.indexOf('.')
      const integer = text.slice(0, dotIndex === -1 ? text.length : dotIndex) || '0'
      if (dotIndex === -1 || decimals === 0) return integer
      const fraction = text.slice(dotIndex + 1).replace(/\./g, '').slice(0, decimals)
      return integer + '.' + fraction
    },
    setNonNegative(option, field, value) {
      this.$set(option, field, this.sanitizeNonNegative(value, field === 'work_seconds' ? 0 : 2))
    },
    preventInvalidNumberKey(event, decimals) {
      if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      if (event.key === '.' && decimals > 0 && !event.target.value.includes('.')) return
      if (!/^\d$/.test(event.key)) event.preventDefault()
    },
    preventInvalidNumberPaste(event, decimals) {
      const value = event.clipboardData.getData('text')
      const pattern = decimals > 0 ? new RegExp('^\\d*(\\.\\d{0,' + decimals + '})?$') : /^\d*$/
      if (!pattern.test(value)) event.preventDefault()
    },
    // 追加一条空白自定义 OPTION
    addCustomOption() {
      this.options.push({
        type: '',
        name: '',
        name_ja: '',
        price: '',
        price_type: 0,
        unit: '元',
        is_overstep: 0,
        auto_calculate: 0,
        remark: '',
        icon_selected: '',
        image: '',
        video: '',
        sort: 0,
        num: 1,
        work_seconds: '',
        images: [],
        videos: [],
        files: [],
        special_requirement: '',
        checked: true
      })
    },
    // 合并系统 OPTION（按名称去重）
    addSystemOptions(list) {
      list.forEach(option => {
        if (!this.options.some(item => item.name === option.name)) {
          this.options.push({ ...option})
        }
      })
    },
    // 上传 OPTION 示例图片（最多 6 张）
    async uploadImage(file, option) {
      if (option.images === undefined) this.$set(option, 'images', [])
      if (option.images.length >= 6) return this.$message.error('最多只能上传6张图片')
      const res = await this.createClient(file)
      option.images.push(res.data)
    },
    // 上传 OPTION 示例视频（最多 3 个）
    async uploadVideo(file, option) {
      if (option.videos === undefined) this.$set(option, 'videos', [])
      if (option.videos.length >= 3) return this.$message.error('最多只能上传3个视频')
      const res = await this.createClient(file)
      option.videos.push(res.data)
    },
    // 上传 OPTION 附件文件（最多 3 个）
    async uploadFile(file, option) {
      if (option.files === undefined) this.$set(option, 'files', [])
      if (option.files.length >= 3) return this.$message.error('最多只能上传3个文件')
      const res = await this.createClient(file)
      option.files.push({ name: file.file.name, url: res.data })
    }
  }
}
</script>

<style lang="scss" scoped>
.product-option-settings { box-sizing: border-box; width: 100%; margin-top: 24px; padding: 24px; border-radius: 8px; background: #fff; }
.section-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.section-title h2 { margin: 0; }
.section-title .el-button { margin-left: 10px; }
.blue-button { border-color: #2c96e7; background: #2c96e7; color: #fff; }
.green-button { border-color: #67c84b; background: #67c84b; color: #fff; }
.red-button { border-color: #b4272d; background: #b4272d; color: #fff; }
.option-table { width: 100%; overflow: hidden;  border-radius: 4px; background: #fff; }
.option-grid { display: flex; width: 100%; }
.option-grid > div { box-sizing: border-box; display: flex; flex: 1 1 0; align-items: center; justify-content: center; min-width: 0; padding: 12px; }
.option-grid > div:nth-child(1) { flex: .8 1 0; }
.option-grid > div:nth-child(2) { flex: 1.7 1 0; }
.option-grid > div:nth-child(3) { flex: 1.3 1 0; }
.option-grid > div:nth-child(4) { flex: .9 1 0; }
.option-grid > div:nth-child(6) { flex: .65 1 0; }
.option-grid > div:nth-child(8) { flex: 1.15 1 0; }
.option-grid > div:nth-child(9) { flex: .55 1 0; }
.table-head { min-height: 46px; background: #edeff4; font-weight: 700; }
.table-row { min-height: 148px; border-top: 1px solid #dcdfe6; }
.name-cell,
.price-cell { gap: 8px; }
.name-cell > span,
.price-cell > span { flex: 0 0 auto; }
.billing-cell { flex-direction: column; align-items: flex-start !important; gap: 10px; }
.billing-cell .el-radio { margin: 0; }
.upload-cell { flex-direction: column; }
.media-list { display: flex; flex-wrap: wrap; gap: 7px; width: 100%; }
.media-item { position: relative; }
.media-item .el-image,
.media-item video { display: block; width: 140px; height: 80px; border-radius: 4px; object-fit: cover; }
.media-item .el-image { width: 80px; height: 80px; }
.video-item { flex: 0 0 140px; }
.media-item button { position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; padding: 0; border: 0; border-radius: 50%; background: #b4272d; color: #fff; cursor: pointer; }
.file-cell { align-items: stretch !important; }
.file-row { display: flex; align-items: center; gap: 6px; width: 100%; margin-bottom: 6px; }
.file-row a { flex: 1; overflow: hidden; color: #0d4fe6; text-overflow: ellipsis; white-space: nowrap; }
.file-row button { padding: 0; border: 0; background: transparent; color: #b4272d; cursor: pointer; }
.delete-option { border: 0; background: transparent; color: #b4272d; cursor: pointer; }
::v-deep .el-upload { display: block; }
</style>
